import { Component, inject } from '@angular/core';
import { ComponentsModule, DialogService, InputOption, ResultDataSource, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { ReactiveFormsModule } from '@angular/forms';
import { MessageService } from '@lib/messages/message.service';
import { createMessageFormGroup, CreateMessageSingleResponse, StoreCreateMessageRequest } from '@lib/api/models/message';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { DialogRef } from '@angular/cdk/dialog';
import { map, shareReplay, take } from 'rxjs';
import { ResultObservable, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { User } from '@lib/api/models/user';
import { UserService } from '@lib/user/user.service';

@Component({
    selector: 'app-create-message',
    standalone: true,
    imports: [
        ComponentsModule,
        ReactiveFormsModule
    ],
    templateUrl: './create-message.component.html',
    styleUrl: './create-message.component.scss'
})
export class CreateMessageComponent {
    private readonly _messageService = inject(MessageService);
    private readonly _snackBar = inject(SnackBarService);
    private readonly _userService = inject(UserService);

    protected readonly messageFormGroup = createMessageFormGroup();
    protected readonly dialogRef = inject<DialogRef>(DialogRef);

    protected readonly users$: ResultObservable<InputOption<User>[]> = this._userService.getAllPossibleUsers().pipe(
        map((response): InputOption<User>[] => {
            return (response?.data ?? []).map(user => ({
                value: user,
                label: `${user.person.given_name} ${user.person.family_name}`,
                testId: user.id
            }));
        }),
        toResult(),
        shareReplay(1)
    );

    protected searchableUsersDataSource = new ResultDataSource<User>({
        source: this.users$,
        searchable: true,
        compareValue: (a, b) => a.id === b.id
    });

    addMessageRequest = new SendableRequest<CreateMessageSingleResponse, StoreCreateMessageRequest>({
        request: (payload) => this._messageService.createMessage(payload!),

        onSend: () => {
            this.messageFormGroup.disable();
        },

        onSuccess: () => {
            this.messageFormGroup.enable();
            this._snackBar.open({
                colour: 'success',
                icon: 'check',
                title: 'Message Sent'
            });
            this._messageService.getAllMessages().pipe(take(1)).subscribe(() => {
                this.dialogRef.close(true);
            });
        },

        onError: () => {
            this.messageFormGroup.enable();
        }
    });

    create() {
        if (this.addMessageRequest.loading()) {
            return;
        }

        const createMessageData: StoreCreateMessageRequest = {
            content: this.messageFormGroup.controls.content.value || '',
            subject: this.messageFormGroup.controls.subject.value || '',
            recipients: this.messageFormGroup.controls.recipients.value || []
        };

        this.addMessageRequest.send(createMessageData);
    }

    public static show(dialog: DialogService) {
        return dialog.openDialog(CreateMessageComponent, {});
    }
}
